import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/auth";
import { hasPermission } from "../../access/permissions";
import {
  canExportOrderActivity,
  canRestoreOrderActivity,
  canViewOrderActivity,
} from "../../utils/roles";
import {
  exportOrderActivity,
  fetchOrderActivity,
  fetchOrderActivityDetail,
  fetchOrderActivityMeta,
  restoreOrderActivity,
} from "../../api/OrderActivityAPIs";
import { ArrowRight, Download, RotateCcw, Search } from "lucide-react";

const CAIRO = "Africa/Cairo";

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    timeZone: CAIRO,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const resultClass = (result) => {
  if (result === "Success") return "bg-emerald-100 text-emerald-800";
  if (result === "PartialSuccess") return "bg-amber-100 text-amber-800";
  if (result === "Failure") return "bg-rose-100 text-rose-800";
  return "bg-slate-100 text-slate-700";
};

const emptyFilters = {
  search: "",
  from: "",
  to: "",
  actor: "",
  actionType: "",
  source: "",
  result: "",
  previousStatus: "",
  newStatus: "",
  orderId: "",
  batchId: "",
  bulkOnly: false,
};

export default function OrderActivityLogPage() {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canExport = canExportOrderActivity(authState.role);
  const canRestore = canRestoreOrderActivity(authState.role);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [restoreKey, setRestoreKey] = useState("");
  const [restoreResult, setRestoreResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const canView = canViewOrderActivity(authState.role) || hasPermission(authState.role, authState.permissions, "orders.activity-log.view");

  const query = useMemo(() => ({
    page,
    pageSize,
    sort: "desc",
    search: filters.search || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    actor: filters.actor || undefined,
    actionType: filters.actionType || undefined,
    source: filters.source || undefined,
    result: filters.result || undefined,
    previousStatus: filters.previousStatus || undefined,
    newStatus: filters.newStatus || undefined,
    orderId: filters.orderId || undefined,
    batchId: filters.batchId || undefined,
    bulkOnly: filters.bulkOnly || undefined,
  }), [filters, page, pageSize]);

  useEffect(() => {
    if (!canView) return;
    fetchOrderActivityMeta().then(setMeta).catch(() => setMeta(null));
  }, [authState.role]);

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchOrderActivity(query)
      .then((pageResult) => {
        if (!cancelled) setData(pageResult);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load activity.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authState.role, query]);

  const openDetail = async (actionId) => {
    setRestoreResult(null);
    setConfirming(false);
    setSelected([]);
    const loaded = await fetchOrderActivityDetail(actionId);
    setDetail(loaded);
    const eligible = (loaded.orders || []).filter((row) => row.restoreSupported).map((row) => row.orderId);
    setSelected(eligible);
  };

  const applyShortcut = (patch) => {
    setPage(1);
    setFilters({ ...emptyFilters, ...patch });
  };

  const updateFilter = (key, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const startRestore = () => {
    setRestoreKey(crypto.randomUUID());
    setConfirming(true);
  };

  const submitRestore = async () => {
    if (!detail?.activity?.actionId) return;
    setBusy(true);
    setError("");
    try {
      const response = await restoreOrderActivity(detail.activity.actionId, selected, restoreKey);
      setRestoreResult(response);
      setConfirming(false);
      const refreshed = await fetchOrderActivityDetail(detail.activity.actionId);
      setDetail(refreshed);
      const pageResult = await fetchOrderActivity(query);
      setData(pageResult);
    } catch (err) {
      setError(err.message || "Restore failed.");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    setError("");
    try {
      await exportOrderActivity({ ...query, page: undefined, pageSize: undefined });
    } catch (err) {
      setError(err.message || "Export failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 text-slate-700" dir="rtl">
        هذا السجل متاح لحسابات لوحة الإدارة فقط.
      </div>
    );
  }

  const total = data?.totalCount || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const rows = (detail?.orders || []).filter((row) => selected.includes(row.orderId) && row.restoreSupported);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 text-sm text-slate-600">
            <ArrowRight size={16} />
            لوحة التحكم
          </button>
          {canExport && (
            <button type="button" onClick={download} disabled={busy} className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-xl px-4 py-2 text-sm">
              <Download size={16} />
              تصدير CSV
            </button>
          )}
        </div>

        <div>
          <h1 className="text-2xl font-bold">سجل نشاط الطلبات</h1>
          <p className="text-sm text-slate-500 mt-1">
            الأوقات بتوقيت القاهرة. السجل يبدأ من لحظة تفعيل هذه الصفحة، والعمليات الأقدم غير معاد بناؤها.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Shortcut label="اتنقلوا إلى Old" onClick={() => applyShortcut({ newStatus: "Old" })} />
          <Shortcut label="تغيير حالة جماعي" onClick={() => applyShortcut({ bulkOnly: true, actionType: "BulkStatusChanged" })} />
          <Shortcut label="عمليات فاشلة" onClick={() => applyShortcut({ result: "Failure,PartialSuccess" })} />
          <Shortcut label="إلغاءات حديثة" onClick={() => applyShortcut({ newStatus: "Cancelled,Canceled,Cancelled by Customer" })} />
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="md:col-span-2 text-sm">
            بحث
            <span className="mt-1 flex items-center gap-2 border border-slate-200 rounded-xl px-3">
              <Search size={16} className="text-slate-400" />
              <input
                className="w-full py-2 outline-none"
                placeholder="رقم الطلب، اسم المنفذ، أو معرف العملية"
                value={filters.search}
                onChange={(event) => updateFilter("search", event.target.value)}
              />
            </span>
          </label>
          <Field label="من" type="date" value={filters.from} onChange={(value) => updateFilter("from", value)} />
          <Field label="إلى" type="date" value={filters.to} onChange={(value) => updateFilter("to", value)} />
          <Field label="المنفذ" value={filters.actor} onChange={(value) => updateFilter("actor", value)} />
          <Select label="نوع العملية" value={filters.actionType} options={meta?.actionTypes || []} onChange={(value) => updateFilter("actionType", value)} />
          <Select label="المصدر" value={filters.source} options={meta?.sources || []} onChange={(value) => updateFilter("source", value)} />
          <Select label="النتيجة" value={filters.result} options={meta?.results || []} onChange={(value) => updateFilter("result", value)} />
          <Select label="الحالة السابقة" value={filters.previousStatus} options={meta?.statuses || []} onChange={(value) => updateFilter("previousStatus", value)} />
          <Select label="الحالة الجديدة" value={filters.newStatus} options={meta?.statuses || []} onChange={(value) => updateFilter("newStatus", value)} />
          <Field label="رقم الطلب" value={filters.orderId} onChange={(value) => updateFilter("orderId", value)} />
          <label className="text-sm flex items-end gap-2 pb-2">
            <input type="checkbox" checked={filters.bulkOnly} onChange={(event) => updateFilter("bulkOnly", event.target.checked)} />
            العمليات الجماعية فقط
          </label>
        </div>

        {error && <div className="bg-rose-50 text-rose-800 rounded-xl px-4 py-3 text-sm">{error}</div>}

        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-right px-3 py-3">الوقت</th>
                <th className="text-right px-3 py-3">العملية</th>
                <th className="text-right px-3 py-3">المنفذ</th>
                <th className="text-right px-3 py-3">النوع</th>
                <th className="text-right px-3 py-3">الطلبات</th>
                <th className="text-right px-3 py-3">المصدر</th>
                <th className="text-right px-3 py-3">النتيجة</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">جاري التحميل...</td></tr>
              )}
              {!loading && (data?.items || []).length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">لا توجد عمليات مطابقة.</td></tr>
              )}
              {(data?.items || []).map((item) => (
                <tr key={item.actionId} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(item.actionId)}>
                  <td className="px-3 py-3 whitespace-nowrap">{formatWhen(item.occurredAtUtc)}</td>
                  <td className="px-3 py-3">
                    <div>{item.description}</div>
                    <div className="text-xs text-slate-500">
                      {item.orderId ? `طلب #${item.orderId}` : item.batchId ? `دفعة ${String(item.batchId).slice(0, 8)}` : ""}
                      {item.previousStatus || item.newStatus ? ` · ${item.previousStatus || "—"} → ${item.newStatus || "—"}` : ""}
                    </div>
                  </td>
                  <td className="px-3 py-3">{item.actorDisplayName || item.actorType}</td>
                  <td className="px-3 py-3">{item.actionType}</td>
                  <td className="px-3 py-3">{item.affectedCount}</td>
                  <td className="px-3 py-3">{item.source}</td>
                  <td className="px-3 py-3"><span className={`rounded-full px-2 py-1 text-xs ${resultClass(item.result)}`}>{item.result}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span>{total} عملية</span>
          <div className="flex items-center gap-2">
            <select value={pageSize} onChange={(event) => { setPage(1); setPageSize(Number(event.target.value)); }} className="border border-slate-200 rounded-lg px-2 py-1">
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)} className="px-3 py-1 border rounded-lg disabled:opacity-40">السابق</button>
            <span>{page} / {pageCount}</span>
            <button type="button" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)} className="px-3 py-1 border rounded-lg disabled:opacity-40">التالي</button>
          </div>
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-slate-900/40 flex justify-center items-end md:items-center p-4 z-40" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-4" dir="rtl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">{detail.activity.description}</h2>
                <p className="text-sm text-slate-500 mt-1">{formatWhen(detail.activity.occurredAtUtc)} · {detail.activity.actorDisplayName} · {detail.activity.source}</p>
              </div>
              <button type="button" onClick={() => setDetail(null)} className="text-slate-500">إغلاق</button>
            </div>
            <div className="text-xs text-slate-500 space-y-1">
              <div>Action ID: {detail.activity.actionId}</div>
              {detail.activity.batchId && <div>Batch ID: {detail.activity.batchId}</div>}
              {detail.correlationId && <div>Correlation: {detail.correlationId}</div>}
            </div>
            {detail.activity.errorMessage && <div className="text-sm text-rose-700">خطأ: {detail.activity.errorMessage}</div>}
            {(detail.changes || []).length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="text-slate-500"><th className="text-right">الحقل</th><th className="text-right">قبل</th><th className="text-right">بعد</th></tr></thead>
                <tbody>
                  {detail.changes.map((change) => (
                    <tr key={change.field} className="border-t">
                      <td className="py-2">{change.field}</td>
                      <td className="py-2">{change.oldValue || "—"}</td>
                      <td className="py-2">{change.newValue || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="space-y-2">
              {(detail.orders || []).map((row) => (
                <label key={row.actionId} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-3 py-2 text-sm">
                  <span className="flex items-center gap-2">
                    {canRestore && row.restoreSupported && (
                      <input
                        type="checkbox"
                        checked={selected.includes(row.orderId)}
                        onChange={(event) => {
                          setSelected((current) => event.target.checked
                            ? [...current, row.orderId]
                            : current.filter((id) => id !== row.orderId));
                        }}
                      />
                    )}
                    <button type="button" className="text-indigo-700 underline" onClick={() => navigate(`/search-orders?orderId=${row.orderId}`)}>
                      طلب #{row.orderId}
                    </button>
                    <span>{row.previousStatus || "—"} → {row.newStatus || "—"}</span>
                  </span>
                  <span className={`rounded-full px-2 py-1 text-xs ${resultClass(row.result)}`}>{row.applied ? row.result : "Not applied"}</span>
                </label>
              ))}
            </div>
            {detail.restoreUnavailableReason && !detail.restoreSupported && (
              <p className="text-sm text-slate-600">{detail.restoreUnavailableReason} الاسترجاع التلقائي لا يلغي الشحن أو الطباعة أو الدفع.</p>
            )}
            {canRestore && detail.restoreSupported && (
              <button type="button" onClick={startRestore} disabled={selected.length === 0} className="inline-flex items-center gap-2 bg-indigo-700 text-white rounded-xl px-4 py-2 disabled:opacity-40">
                <RotateCcw size={16} />
                استرجاع الحالة السابقة
              </button>
            )}
            {!canRestore && <p className="text-sm text-slate-500">الاسترجاع والتصدير متاحان لحساب المالك فقط.</p>}
            {restoreResult && (
              <div className="text-sm bg-slate-50 rounded-xl p-3 space-y-1">
                <div>تم الاسترجاع: {restoreResult.restored?.length || 0}</div>
                <div>تم التخطي: {restoreResult.skipped?.length || 0}</div>
                <div>فشل: {restoreResult.failed?.length || 0}</div>
                {(restoreResult.skipped || []).map((item) => (
                  <div key={`skip-${item.orderId}`}>طلب #{item.orderId}: {item.message}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" dir="rtl">
            <h3 className="text-lg font-bold">تأكيد استرجاع الحالة</h3>
            <p className="text-sm text-slate-600">
              سيتم تغيير حالة الطلبات المحددة فقط إذا كانت حالتها الحالية ما زالت هي الحالة التي نتجت عن هذه العملية.
              لن يتم عكس الشحن أو إرسال Printlet أو أي عملية دفع أو استرداد.
            </p>
            <ul className="text-sm space-y-1">
              {rows.map((row) => (
                <li key={row.orderId}>طلب #{row.orderId}: {row.newStatus} ← {row.previousStatus}</li>
              ))}
            </ul>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setConfirming(false)} className="px-4 py-2 border rounded-xl">إلغاء</button>
              <button type="button" disabled={busy} onClick={submitRestore} className="px-4 py-2 bg-indigo-700 text-white rounded-xl">تأكيد الاسترجاع</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Shortcut({ label, onClick }) {
  return (
    <button type="button" onClick={onClick} className="bg-white border border-slate-200 rounded-full px-3 py-1.5 text-sm hover:border-slate-400">
      {label}
    </button>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <label className="text-sm">
      {label}
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2" />
    </label>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <label className="text-sm">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 bg-white">
        <option value="">الكل</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}
